import { useEffect, useRef, useState } from 'react';

export interface PaginaLida<T> {
  itens: T[];
  total: number;
  /** Prefixo que o servidor usou na leitura, quando ele devolve (reconhecimentos, AJ3-06). */
  prefixo?: number;
}

export type EstadoDaLista = 'carregando' | 'erro' | 'vazio' | 'pronto';

interface Leitura<T> extends Partial<PaginaLida<T>> {
  chave: string;
  erro?: boolean;
}

/**
 * Leitura paginada das páginas "Ver todos do prefixo" (AJ3-01, AJ3-09). Sem `ler` (ciclo ou prefixo ainda não
 * carregaram), espera sem pedir. Troca de filtro volta à página 1. A resposta só vale para a chave que a pediu:
 * a de uma página ou filtro antigo é descartada. Uma página depois da primeira que volta vazia com itens no total (itens
 * apagados entre uma leitura e outra) leva à última página que ainda existe, em vez de prender a pessoa no vazio.
 */
export function useListaDoPrefixo<T>(
  ler: ((pagina: number) => Promise<PaginaLida<T>>) | undefined,
  filtro: string,
  porPagina: number,
) {
  const [pagina, setPagina] = useState(0);
  const [tentativa, setTentativa] = useState(0);
  const [leitura, setLeitura] = useState<Leitura<T>>();
  const lerRef = useRef(ler);
  lerRef.current = ler;

  const [filtroDaPagina, setFiltroDaPagina] = useState(filtro);
  if (filtroDaPagina !== filtro) {
    setFiltroDaPagina(filtro);
    setPagina(0);
  }

  const podeLer = !!ler;
  const chave = `${filtro}|${pagina}|${tentativa}`;

  useEffect(() => {
    if (!podeLer || !lerRef.current) return;

    let ativo = true;
    lerRef.current(pagina)
      .then(lida => ativo && setLeitura({ chave, ...lida }))
      .catch(() => ativo && setLeitura({ chave, erro: true }));

    return () => {
      ativo = false;
    };
    // `chave` já reúne filtro, página e tentativa.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [podeLer, chave]);

  const atual = leitura?.chave === chave ? leitura : undefined;
  const itens = atual?.itens ?? [];
  const total = atual?.total ?? 0;

  // AJ3-07/13: a pagina diminui a cada passo, então isto não entra em laço; a leitura nova mostra o "carregando".
  if (atual && !atual.erro && !itens.length && pagina > 0 && total > 0) {
    setPagina(Math.min(pagina - 1, Math.ceil(total / porPagina) - 1));
  }
  let estado: EstadoDaLista = 'carregando';
  if (atual?.erro) estado = 'erro';
  else if (atual) estado = itens.length ? 'pronto' : 'vazio';

  return {
    estado,
    itens,
    total,
    prefixo: atual?.prefixo,
    pagina,
    irParaPagina: setPagina,
    tentarDeNovo: () => setTentativa(valor => valor + 1),
  };
}
